import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef,
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy, IReportVariantDef } from "@components/smart/smartTable";
import BindingContext from "@odata/BindingContext";
import { GrantStatusCode } from "@odata/GeneratedEnums";
import { getThemeColorForGrantStatus } from "@pages/grant/Grant.utils";
import { IAppContext } from "contexts/appContext/AppContext.types";
import { FieldType, Sort } from "enums";
import { ColoredText } from "global.style";
import i18next, { t } from "i18next";
import React from "react";
import { ROUTE_BUSINESS_PARTNER, ROUTE_NONPROFIT_GRANT } from "routes";

import { getDocumentNumberOursOverride } from "../CommonDefs";
import { IReportTableDefinition, TReportColumnOverrides } from "../Report.utils";
import { ReportId } from "../ReportIds";
import { ReportStorage } from "../ReportStorage";

const GRANT_JOURNAL_PATH = "GrantJournal";

export const GrantsProps = {
    grantStatuses: BindingContext.localContext("GrantStatuses"),
};

export const GrantsVariant = {
    Evala: "-182",
    Grants: "-183",
} as const;

export type TGrantsVariant = (typeof GrantsVariant)["Evala"] | (typeof GrantsVariant)["Grants"];

export const getDefinition = (context: IAppContext): IReportTableDefinition => {
    const tableId = ReportId.GrantJournal;
    const title = i18next.t("Reporting:GrantJournal.Title");
    const path = GRANT_JOURNAL_PATH;
    const initialSortBy = [{ id: "GrantJournal_ShortName", sort: Sort.Asc }];
    const parameters: string[] = [GrantsProps.grantStatuses];

    const columnOverrides: TReportColumnOverrides = {
        GrantStatus_Code: {
          
            formatter: (value) => {
                if(!value) {
                    return null;
                }
                const color = getThemeColorForGrantStatus(value as GrantStatusCode);
                const translatedText = t(`Reporting:Columns.GrantStatus_${value}`);
                return {
                    value: <ColoredText color={color}> {translatedText} </ColoredText>,
                    tooltip: translatedText,
                };
            },
            
        },

        GrantJournal_DocumentNumberOurs: getDocumentNumberOursOverride({
            documentTypeProperty: "GrantJournal_DocumentTypeCode",
            documentIdProperty: "GrantJournal_DocumentId",
        }),
        GrantJournal_GrantNumberOurs: {
            isDisabled: true,
        },
        DocumentBusinessPartner_Name: {
            formatter: (val, args) => {
            
                const businessPartnerId = args.entity["GrantJournal_DocumentBusinessPartnerId"];
                if(!businessPartnerId) {
                    return {
                        value: val,
                        tooltip: null,
                    };
                }
                const url = `${ROUTE_BUSINESS_PARTNER}/${businessPartnerId}`;

                return getTableIntentLink(val?.toString(), {
                    route: url,
                    context: args.storage.context,
                    storage: args.storage,
                });
            }
        },
        GrantBusinessPartner_Name: {
            formatter: (val, args) => {
            
                const businessPartnerId = args.entity["GrantJournal_GrantBusinessPartnerId"];
                if(!businessPartnerId) {
                    return {
                        value: val,
                        tooltip: null,
                    };
                }
                const url = `${ROUTE_BUSINESS_PARTNER}/${businessPartnerId}`;

                return getTableIntentLink(val?.toString(), {
                    route: url,
                    context: args.storage.context,
                    storage: args.storage,
                });
            }
        },

        GrantJournal_ShortName: {
            formatter: (val, args) => {
                const grantId = args.entity["GrantJournal_GrantId"];
                const url = `${ROUTE_NONPROFIT_GRANT}/${grantId}`;

                return getTableIntentLink(val?.toString(), {
                    route: url,
                    context: args.storage.context,
                    storage: args.storage,
                });
            },
        },
        GrantJournal_RequiredParticipation: {
            formatter: (value) => {
                return {
                    value: value ? `${value} %` : null,
                    tooltip: null
                };
            }
        }
    };

    const defaultReportHierarchy: IReportHierarchy = {
        Aggregate: false,
        Groups: [],
        Columns: [
            { ColumnAlias: "GrantJournal_ShortName" },
            { ColumnAlias: "GrantStatus_Code" },
            { ColumnAlias: "GrantJournal_DocumentNumberOurs" },

            { ColumnAlias: "GrantJournal_NumberTheirs" },
            { ColumnAlias: "GrantJournal_DateIssued" },
            { ColumnAlias: "DebitAccount_Number" },
            { ColumnAlias: "CreditAccount_Number" },
            { ColumnAlias: "GrantJournal_Description" },
            { ColumnAlias: "GrantJournal_DocumentItemAmount" },
            { ColumnAlias: "GrantJournal_GrantEntryAmount" },
            { ColumnAlias: "GrantJournal_GrantEntryParticipation" },
            { ColumnAlias: "DateClearing" },
        ],
        Aggregations: [],
    };

    const defaultReportVariants: Record<TGrantsVariant, IReportVariantDef> = {
        [GrantsVariant.Evala]: {
            ReportHierarchy: defaultReportHierarchy,
        },
        [GrantsVariant.Grants]: {
            ReportHierarchy: {
                Aggregate: true,
                Groups: [
                    {
                        ColumnAlias: "GrantJournal_ShortName",
                    },
                ],
                Columns: [
                    { ColumnAlias: "GrantStatus_Code" },
                    { ColumnAlias: "GrantJournal_DocumentNumberOurs" },

                    { ColumnAlias: "GrantJournal_NumberTheirs" },
                    { ColumnAlias: "GrantJournal_DateIssued" },
                    { ColumnAlias: "DebitAccount_Number" },
                    { ColumnAlias: "CreditAccount_Number" },
                    { ColumnAlias: "GrantJournal_Description" },
                    { ColumnAlias: "GrantJournal_DocumentItemAmount" },
                    { ColumnAlias: "GrantJournal_GrantEntryAmount" },
                    { ColumnAlias: "GrantJournal_GrantEntryParticipation" },
                    { ColumnAlias: "GrantJournal_RequiredParticipation" },
                    { ColumnAlias: "DateClearing" },
                ],
                Aggregations: [],
            },
        },
    };

    const filterBarDef: IFilterGroupDef[] = [
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
            defaultFilters: [GrantsProps.grantStatuses],
            filterDefinition: {
                [GrantsProps.grantStatuses]: {
                    id: GrantsProps.grantStatuses,
                    fieldSettings: {
                        itemsFactory: async () => {
                            const items: ISelectItem[] = Object.entries(GrantStatusCode).map(
                                (entry) =>
                                    ({
                                        id: entry[0],
                                        label: t(`Reporting:Columns.GrantStatus_${entry[0]}`),
                                    } as ISelectItem)
                            );
                            return items;
                        },
                    },
                    label: t('Reporting:Columns.GrantStatus_Code'),
                    type: FieldType.MultiSelect,
                    defaultValue: () => [GrantStatusCode.InProgress, GrantStatusCode.Applied],
                },
            },
        },
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            allowCustomFilters: false,
            defaultFilters: [],
            filterDefinition: {},
        },
    ];

    const onBeforeLoad = async (storage: ReportStorage) => {};

    return {
        title,
        path,
        id: tableId,
        initialSortBy,
        filterBarDef,
        parameters,
        columnOverrides,
        onBeforeLoad,
        defaultReportHierarchy,
        defaultReportVariants,
    };
};

